import React, { useEffect, useState, useContext } from 'react';
import { useParams, useHistory } from 'react-router';
import CustomContent from '../../../components/Content/CustomContent';
import { Button, Card, Checkbox, Col, Divider, Form, Input, Row, Select, Typography, Space, Spin } from 'antd';
import Section from './Section';
import { v4 as uuidv4 } from 'uuid';
import { saveData, fetchData } from '../../service/dataServices';
import TranslatableInput from '../../components/form/TranslatableInput';
import { useTranslation } from 'react-i18next';
import UserContext from '../../../pages/layout/UserContext';
import checkPermission from '../../../components/Authorization/Util';
import ForbiddenResult from '../../../components/Authorization/ForbiddenResult';
import FormPreviewModal from './FormPreviewModal';

const { Title, Text } = Typography;

export default function CustomFormEdit() {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();
  const currentUser = useContext(UserContext);

  const hasWritePerm = checkPermission(currentUser, 'formSchema', 'write');

  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sectionName, setSectionName] = useState({ en: "", ar: "" });
  const [activeSections, setActiveSections] = useState([]);
  const [sectionDataMap, setSectionDataMap] = useState({});
  //const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [hasConflict, setHasConflict] = useState(false);
  const [forceSave, setForceSave] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Fetch categories
  //fetchData('/api/dropdowns/categories', response => setCategories(response));
  fetchData('/api/dropdowns', response => setGroups(response.group || []));

  // Fetch existing schema data
  fetchData(`/api/form-schema/${id}`, (data) => {
    setSchema(data);
    setLoading(false);

    // Pre-populate form fields
    form.setFieldsValue({
      formName: data.name,
      formIdPrefix: data.formIdPrefix,
      group: data.groupId,
    });

    // Pre-populate sections from existing schema data
    if (data.sections && data.sections.length > 0) {
      const sections = data.sections.map(section => ({
        id: section.id || uuidv4(),
        name: section.title || { en: "", ar: "" },
      }));
      setActiveSections(sections);

      // Pre-populate sectionDataMap with existing section data
      const dataMap = {};
      data.sections.forEach(section => {
        const sectionId = section.id || uuidv4();
        dataMap[sectionId] = {
          id: sectionId,
          title: section.title || { en: "", ar: "" },
          rows: section.rows || [],
        };
      });
      setSectionDataMap(dataMap);
    }
  }, undefined, !!id);

  const onAddSection = () => {
    const hasContent = sectionName.en?.trim() || sectionName.ar?.trim();
    if (!hasContent) return;
    const newId = uuidv4();
    setActiveSections(prev => [...prev, { id: newId, name: sectionName }]);
    setSectionName({ en: "", ar: "" });
  };

  const deleteSection = (sectionId) => {
    setActiveSections(prev => prev.filter(s => s.id !== sectionId));
    setSectionDataMap(prev => {
      const updated = { ...prev };
      delete updated[sectionId];
      return updated;
    });
  };

  const saveSectionData = (sectionId, data) => {
    setSectionDataMap(prev => ({ ...prev, [sectionId]: data }));
  };

  const PATH = `/api/form-schema`;
  const saveMutation = saveData(PATH, "PUT");

  function onSubmit() {
    const formValues = form.getFieldsValue();
    const sections = Object.values(sectionDataMap);
    const dataToSend = {
      id: id,
      name: formValues.formName,
      formIdPrefix: formValues.formIdPrefix,
      groupId: formValues.group,
      sections: sections,
    };

    saveMutation.mutate({ dataToSend, id: id, params: forceSave ? { force: true } : undefined }, {
      onSuccess: () => {
        setHasConflict(false);
        setForceSave(false);
        history.push('/show-forms');
      },
      onError: (error) => {
        const status = error?.response?.status;
        const responseData = error?.response?.data;
        if (status === 409 || responseData?.status === 'CONFLICT') {
          setHasConflict(true);
        }
      }
    });
  }

  if (!hasWritePerm) {
    return (
      <CustomContent>
        <ForbiddenResult />
      </CustomContent>
    );
  }

  if (loading) {
    return (
      <CustomContent>
        <Row justify="center" style={{ padding: '60px 0' }}>
          <Spin size="large" />
        </Row>
      </CustomContent>
    );
  }

  return (
    <CustomContent>
      <Row justify="space-between" align="middle">
        <Col><Title level={4}>{t('customForm.editFormTemplate', 'Edit Form Template')}</Title></Col>
        <Col>
          {Object.keys(sectionDataMap).length > 0 && (
            <Button onClick={() => setPreviewOpen(true)}>
              {t('customForm.preview', 'Preview')}
            </Button>
          )}
        </Col>
      </Row>

      <Row justify="center">
        <Col span={24}>
          <Card>
            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
            >
              <Row gutter={16} align="bottom">
                <Col span={8}>
                  <Form.Item
                    name="formName"
                    label={t('customForm.formName', 'Form Name')}
                    rules={[{ required: true, message: t('customForm.formNameRequired', 'Please enter a form name') }]}
                    initialValue={{ en: "", ar: "" }}
                  >
                    <TranslatableInput
                      placeholder={{ en: "e.g. Carpet Quality Check", ar: "مثال: فحص جودة السجاد" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="formIdPrefix"
                    label={t('customForm.formIdPrefix', 'Form ID Prefix')}
                    rules={[{ required: true, message: t('customForm.formIdPrefixRequired', 'Please enter a form ID prefix') }]}
                  >
                    <Input placeholder="e.g. 'CT-'" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="group"
                    label={t('customForm.group', 'Group')}
                    rules={[{ required: true, message: t('customForm.groupRequired', 'Please select a group') }]}
                  >
                    <Select
                      placeholder={t('customForm.selectGroup', 'Select a group')}
                      showSearch
                      optionFilterProp="children"
                      allowClear
                    >
                      {groups.map(group => (
                        <Select.Option key={group.id} value={group.id}>
                          {group.title}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Row justify="center" align="bottom" gutter={16}>
          <Col><Text strong>{t('customForm.newSection', 'New Section')}:</Text></Col>
          <Col span={8}>
            <TranslatableInput
              value={sectionName}
              onChange={setSectionName}
              placeholder="e.g. Material Analysis"
            />
          </Col>
          <Col><Button onClick={onAddSection} disabled={!sectionName.en?.trim() && !sectionName.ar?.trim()}>{t('customForm.addSection', 'Add Section')}</Button></Col>
        </Row>
      </Card>

      {activeSections.map((s) => (
        <Section
          key={s.id}
          id={s.id}
          name={s.name}
          deleteFn={deleteSection}
          saveFn={saveSectionData}
          initialRows={sectionDataMap[s.id]?.rows}
        />
      ))}

      {Object.keys(sectionDataMap).length > 0 && (
        <Row justify="center" style={{ padding: '60px 0' }}>
          <Space direction="vertical" align="center">
            <Button
              type="primary"
              size="large"
              onClick={onSubmit}
              loading={saveMutation.isLoading}
            >
              {t('customForm.updateFormTemplate', 'Update Form Template')}
            </Button>
            {hasConflict && (
              <Checkbox
                checked={forceSave}
                onChange={(e) => setForceSave(e.target.checked)}
                style={{ color: '#faad14' }}
              >
                {t('customForm.forceSave', 'Ignore data consistency issue and force save')}
              </Checkbox>
            )}
          </Space>
        </Row>
      )}
      <FormPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        formName={form.getFieldValue('formName')}
        sections={Object.values(sectionDataMap)}
      />
    </CustomContent>
  );
}